import { UseFormRegister, FieldErrors } from 'react-hook-form'
import { formatRegistrationDropdownPrice } from '@/utils/currency'
import { getRegistrationOptionHighlight } from '../registrationOptionCopy'

interface FormData {
  registrants: number
  registrationOptionId: number | null
}

interface RegistrationOption {
  id?: number | null
  description?: string | null
  price?: number | null
  salePrice?: number | null
  priceIncVat?: number | null
  salePriceIncVat?: number | null
  inStock?: boolean | null
  currency?: {
    symbol?: string | null
  } | null
}

interface RegistrationTypeSelectorProps {
  register: UseFormRegister<FormData>
  errors: FieldErrors<FormData>
  isSubmitted: boolean
  registrationOptions: RegistrationOption[]
  requiresOption: boolean
  defaultOptionId: number | null
  selectedOptionId: number | null | undefined
  currencySymbol: string
  conventionSlug: string
  onOptionSelected?: (optionId: number, optionName: string | null) => void
}

const getOptionPriceText = (
  option: RegistrationOption | undefined,
  currencySymbol: string
) => {
  const price =
    option?.salePriceIncVat ??
    option?.priceIncVat ??
    option?.salePrice ??
    option?.price
  if (!price) return ''

  // Partner pricing is halved for display on this selection page only.
  const isPartnerOption = option?.description?.toLowerCase().includes('partner')
  const displayPrice = isPartnerOption ? price / 2 : price
  return `${currencySymbol}${formatRegistrationDropdownPrice(displayPrice)}${isPartnerOption ? '/person' : ''}`
}

export const RegistrationTypeSelector = ({
  register,
  errors,
  isSubmitted,
  registrationOptions,
  requiresOption,
  defaultOptionId,
  selectedOptionId,
  currencySymbol,
  conventionSlug,
  onOptionSelected,
}: RegistrationTypeSelectorProps) => {
  const fieldLabel = requiresOption
    ? 'What type of ticket would you like?'
    : 'Registration Type'
  const normalizedSelectedOptionId =
    selectedOptionId == null ? null : Number(selectedOptionId)
  const validation = {
    validate: (value: number | null) => {
      if (requiresOption && (value === null || value === undefined)) {
        return 'Please select a registration option'
      }
      return true
    },
    setValueAs: (value: string | null | undefined) => {
      if (value === '' || value === null || value === undefined) return null
      const numberValue = Number(value)
      return Number.isNaN(numberValue) ? null : numberValue
    },
  }

  if (!requiresOption) {
    const option = registrationOptions[0]
    const priceText = getOptionPriceText(option, currencySymbol)

    return (
      <div className="rounded-lg border-2 border-gray-200 bg-white p-6 shadow-sm">
        <input
          type="hidden"
          value={defaultOptionId ?? ''}
          {...register('registrationOptionId', validation)}
        />
        <p className="text-lg font-semibold text-gray-900">{fieldLabel}</p>
        <div className="mt-3 flex items-center justify-between gap-4 rounded-lg bg-gray-50 p-4">
          <span className="font-semibold text-gray-900">
            {option?.description || 'Standard Registration'}
          </span>
          {priceText && (
            <span className="text-xl font-bold text-gray-950">{priceText}</span>
          )}
        </div>
      </div>
    )
  }

  return (
    <fieldset
      className="rounded-lg border-2 border-gray-200 bg-white p-6 shadow-sm"
      aria-describedby={
        isSubmitted && errors.registrationOptionId
          ? 'registration-option-error'
          : undefined
      }
    >
      <legend className="px-1 text-lg font-semibold text-gray-900">
        {fieldLabel}
      </legend>
      <div className="mt-2 grid grid-cols-1 gap-4">
        {registrationOptions.map((option, index) => {
          const priceText = getOptionPriceText(option, currencySymbol)
          const optionId = option.id ?? null
          const inputId = `registrationOptionId-${optionId ?? index}`
          const highlight = getRegistrationOptionHighlight(
            conventionSlug,
            option.description
          )
          const registrationField = register('registrationOptionId', validation)

          return (
            <label
              key={optionId ?? inputId}
              htmlFor={inputId}
              className="relative block cursor-pointer"
            >
              <input
                {...registrationField}
                id={inputId}
                type="radio"
                value={optionId ?? ''}
                checked={optionId === normalizedSelectedOptionId}
                className="peer sr-only"
                onChange={event => {
                  registrationField.onChange(event)
                  const selectedOptionId = Number(event.currentTarget.value)
                  if (Number.isFinite(selectedOptionId)) {
                    onOptionSelected?.(
                      selectedOptionId,
                      option.description ?? null
                    )
                  }
                }}
              />
              <svg
                aria-hidden="true"
                className="pointer-events-none absolute bottom-4 right-4 z-10 size-6 rounded-full bg-vi-red p-1 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.704 5.293a1 1 0 0 1 .003 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.411 0Z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="block h-full rounded-xl border-2 border-gray-300 p-5 transition-colors duration-200 peer-checked:border-vi-red peer-checked:bg-red-50 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-vi-red">
                <span className="flex items-start justify-between gap-3">
                  <span className="text-lg font-bold text-gray-950">
                    {option.description || 'Standard Registration'}
                  </span>
                  {priceText && (
                    <span className="text-xl font-bold text-gray-950">
                      {priceText}
                    </span>
                  )}
                </span>
                {highlight && (
                  <span className="mt-3 block text-sm leading-relaxed text-gray-700">
                    {highlight}
                  </span>
                )}
                <span className="mt-4 block pr-8 text-sm font-semibold text-gray-500">
                  Select this ticket
                </span>
              </span>
            </label>
          )
        })}
      </div>
      {isSubmitted && errors.registrationOptionId && (
        <p
          id="registration-option-error"
          className="mt-3 text-sm text-red-600"
        >
          {errors.registrationOptionId.message}
        </p>
      )}
    </fieldset>
  )
}
